import { StandardCurrency } from 'base/StandardCurrency/StandardCurrency';
import StandardInput from 'base/StandardInput/StandardInput';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { Card } from 'react-bootstrap';
import { convertToCurrency, parseIntoNumber } from 'utils/helpers';

export interface calculationDataI {
  adult_net_income: number;
  additional_income: number;
  childcare_allowance: number;
  alimony_received: number;
  childen_net_income: number;
  family_bonus: number;
  alimony_paid: number;
  adults_in_household: number;
  children_in_household: number;
  household_income: number;
  household_income_per_adult: number;
}
interface WithIncomeCalculatorPropsI {
  data?: calculationDataI;
  disable?: boolean;
}
export const WithIncomeCalculator = (props: WithIncomeCalculatorPropsI) => {
  const [calculationData, setCalculationData] = useState<calculationDataI>(
    props.data ?? {
      adult_net_income: 3500,
      additional_income: 250,
      childcare_allowance: 200,
      alimony_received: 100,
      childen_net_income: 100,
      family_bonus: 400,
      alimony_paid: 200,
      adults_in_household: 2,
      children_in_household: 1,
      household_income: 0,
      household_income_per_adult: 0
    }
  );

  useEffect(() => {
    if (props.data) {
      setCalculationData(props.data);
    }
  }, [props.data]);

  useEffect(() => {
    if (props.data) return;

    const {
      adult_net_income,
      additional_income,
      childcare_allowance,
      alimony_received,
      childen_net_income,
      family_bonus,
      alimony_paid,
      adults_in_household,
      children_in_household,
      household_income,
      household_income_per_adult
    } = calculationData;

    const totalIncome =
      adult_net_income +
      additional_income +
      childcare_allowance +
      alimony_received +
      childen_net_income -
      family_bonus -
      alimony_paid;

    const totalHouseholdIncomePerAdult =
      totalIncome / (adults_in_household + children_in_household || 1);

    if (
      household_income !== totalIncome ||
      household_income_per_adult !== totalHouseholdIncomePerAdult
    ) {
      setCalculationData(prevData => ({
        ...prevData,
        household_income: totalIncome,
        household_income_per_adult: totalHouseholdIncomePerAdult
      }));
    }
  }, [calculationData, props.data]);

  return (
    <Card className="w-100 mx-auto">
      <Card.Header>
        <div className="pt-2">
          <h3 className="font-bold mb-1">
            {t('household_income_calculator.title')}
          </h3>
          <p className=" mb-2">
            {t('household_income_calculator.description')}
          </p>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="p-4 mb-3 border rounded bg-gray-100">
          <h3 className="h5 mb-2">
            {t('household_income_calculator.process.title')}
          </h3>

          <ol className="mb-0">
            <li className="mb-1">
              {t('household_income_calculator.process.section_1.title')}
              <ul className="mt-1">
                <li>
                  {t('household_income_calculator.process.section_1.field_1')}
                </li>
                <li>
                  {t('household_income_calculator.process.section_1.field_2')}
                </li>
                <li>
                  {t('household_income_calculator.process.section_1.field_3')}
                </li>
                <li>
                  {t('household_income_calculator.process.section_1.field_4')}
                </li>
                <li>
                  {t('household_income_calculator.process.section_1.field_5')}
                </li>
              </ul>
            </li>
            <li className="mb-1">
              {t('household_income_calculator.process.section_2.title')}
              <ul className="mt-1">
                <li>
                  {t('household_income_calculator.process.section_2.field_1')}
                </li>
                <li>
                  {t('household_income_calculator.process.section_2.field_2')}
                </li>
              </ul>
            </li>
            <li>{t('household_income_calculator.process.devide')}</li>
          </ol>
        </div>
      </Card.Body>
      <Card.Footer>
        <div className="row g-4">
          <div className="col-md-12">
            <div className="mb-2">
              <h4 className="mb-2 pb-1  font-bold">
                {t('household_income_calculator.calculator.income.title')}
              </h4>

              <div className="list-group mb-2">
                <div className="list-group-item d-flex justify-content-between align-items-center bg-gray-100 flex-column flex-md-row">
                  <span>
                    {t('household_income_calculator.process.section_1.field_1')}
                  </span>
                  <span className="col-12 col-md-4">
                    <StandardCurrency
                      className=""
                      onChange={e =>
                        setCalculationData({
                          ...calculationData,
                          adult_net_income: parseIntoNumber(e)
                        })
                      }
                      showError={false}
                      value={calculationData.adult_net_income}
                      validations={[]}
                      disabled={props.disable}
                    />
                  </span>
                </div>

                <div className="list-group-item d-flex justify-content-between align-items-center bg-gray-100 flex-column flex-md-row">
                  <span>
                    {t('household_income_calculator.process.section_1.field_2')}
                  </span>
                  <span className="col-12 col-md-4">
                    <StandardCurrency
                      className=""
                      onChange={e =>
                        setCalculationData({
                          ...calculationData,
                          additional_income: parseIntoNumber(e)
                        })
                      }
                      showError={false}
                      value={calculationData.additional_income}
                      validations={[]}
                      disabled={props.disable}
                    />
                  </span>
                </div>

                <div className="list-group-item d-flex justify-content-between align-items-center bg-gray-100 flex-column flex-md-row">
                  <span>
                    {t('household_income_calculator.process.section_1.field_3')}
                  </span>
                  <span className="col-12 col-md-4">
                    <StandardCurrency
                      className=""
                      onChange={e =>
                        setCalculationData({
                          ...calculationData,
                          childcare_allowance: parseIntoNumber(e)
                        })
                      }
                      showError={false}
                      value={calculationData.childcare_allowance}
                      validations={[]}
                      disabled={props.disable}
                    />
                  </span>
                </div>

                <div className="list-group-item d-flex justify-content-between align-items-center bg-gray-100 flex-column flex-md-row">
                  <span>
                    {t('household_income_calculator.calculator.income.field_4')}
                  </span>
                  <span className="col-12 col-md-4">
                    <StandardCurrency
                      className=""
                      onChange={e =>
                        setCalculationData({
                          ...calculationData,
                          alimony_received: parseIntoNumber(e)
                        })
                      }
                      showError={false}
                      value={calculationData.alimony_received}
                      disabled={props.disable}
                      validations={[]}
                    />
                  </span>
                </div>

                <div className="list-group-item d-flex justify-content-between align-items-center bg-gray-100 flex-column flex-md-row">
                  <span>
                    {t('household_income_calculator.process.section_1.field_5')}
                  </span>
                  <span className="col-12 col-md-4">
                    <StandardCurrency
                      className=""
                      onChange={e =>
                        setCalculationData({
                          ...calculationData,
                          childen_net_income: parseIntoNumber(e)
                        })
                      }
                      showError={false}
                      value={calculationData.childen_net_income}
                      disabled={props.disable}
                      validations={[]}
                    />
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="mb-3 pb-1 font-bold">
                {t('household_income_calculator.calculator.deduction.title')}
              </h4>

              <div className="list-group">
                <div className="list-group-item d-flex justify-content-between align-items-center bg-gray-100 flex-column flex-md-row">
                  <span>
                    {t(
                      'household_income_calculator.calculator.deduction.field_1'
                    )}
                  </span>
                  <span className="col-12 col-md-4">
                    <StandardCurrency
                      className=""
                      onChange={e =>
                        setCalculationData({
                          ...calculationData,
                          family_bonus: parseIntoNumber(e)
                        })
                      }
                      showError={false}
                      value={calculationData.family_bonus}
                      disabled={props.disable}
                      validations={[]}
                    />
                  </span>
                </div>

                <div className="list-group-item d-flex justify-content-between align-items-center bg-gray-100 flex-column flex-md-row">
                  <span>
                    {t(
                      'household_income_calculator.calculator.deduction.field_2'
                    )}
                  </span>
                  <span className="col-12 col-md-4">
                    <StandardCurrency
                      className=""
                      onChange={e =>
                        setCalculationData({
                          ...calculationData,
                          alimony_paid: parseIntoNumber(e)
                        })
                      }
                      showError={false}
                      value={calculationData.alimony_paid}
                      disabled={props.disable}
                      validations={[]}
                    />
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-12">
            <div className="mb-4">
              <h4 className="mb-3 pb-1 font-bold">
                {t('household_income_calculator.calculator.members.title')}
              </h4>

              <div className="list-group mb-2">
                <div className="list-group-item d-flex justify-content-between align-items-center bg-gray-100 flex-column flex-md-row">
                  <span>
                    {t(
                      'household_income_calculator.calculator.members.field_1'
                    )}
                  </span>
                  <span className="col-12 col-md-4">
                    <StandardInput
                      className=" mt-2 "
                      onChange={e => {
                        setCalculationData({
                          ...calculationData,
                          adults_in_household: parseIntoNumber(e)
                        });
                      }}
                      showError={false}
                      value={calculationData.adults_in_household}
                      label=""
                      placeholder=""
                      type=""
                      disabled={props.disable}
                      validations={[]}
                    />
                  </span>
                </div>

                <div className="list-group-item d-flex justify-content-between align-items-center bg-gray-100 flex-column flex-md-row">
                  <span>
                    {t(
                      'household_income_calculator.calculator.members.field_2'
                    )}
                  </span>
                  <span className="col-12 col-md-4">
                    <StandardInput
                      className=" mt-2"
                      onChange={e => {
                        setCalculationData({
                          ...calculationData,
                          children_in_household: parseIntoNumber(e)
                        });
                      }}
                      showError={false}
                      value={calculationData.children_in_household}
                      label=""
                      placeholder=""
                      type=""
                      disabled={props.disable}
                      validations={[]}
                    />
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-light-red p-4 rounded border border-light-red">
              <h3 className="mb-3 pb-1 font-bold">
                {t('household_income_calculator.calculator.result.title')}
              </h3>

              <div className="d-flex justify-content-between align-items-center mb-2">
                <h4 className="mb-0">
                  {t('household_income_calculator.calculator.result.field_1')}
                </h4>
                <span className="h4 mb-0 text-red fw-bold">
                  {convertToCurrency(calculationData.household_income)}
                </span>
              </div>

              <div className="d-flex justify-content-between align-items-center">
                <h4 className=" mb-0 ">
                  {t('household_income_calculator.calculator.result.field_2')}
                </h4>
                <span className="h4 mb-0 text-red fw-bold">
                  {convertToCurrency(
                    calculationData.household_income_per_adult
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      </Card.Footer>
    </Card>
  );
};
